import DOMPurify from 'dompurify';
import type { Client } from '../../models/Client';
import type { Offer, OfferLine, OfferWithLines } from '../../models/Offer';
import { AuditActionType, OfferStatus } from '../../models/enums';
import { DEFAULT_TAX_RATE, OFFER_VALIDITY_DAYS } from '../../config/constants';
import { addDays } from '../../utils/date';
import { generateOfferReference } from '../../utils/offerReference';
import { apiClient } from '../api/apiClient';
import { createId, delay, readCollection, seedCollection, writeCollection } from './dataverseClient';
import { auditLogService } from './auditLogService';
import { clientsSeed, offerLinesSeed, offersSeed } from './mockData';

const OFFER_ENTITY = 'cr_crm_offer';
const OFFER_LINE_ENTITY = 'cr_crm_offerline';

class OfferService {
  // ─── localStorage helpers (fallback) ─────────────────────────────────────

  private ensureSeed(): void {
    seedCollection(OFFER_ENTITY, offersSeed);
    seedCollection(OFFER_LINE_ENTITY, offerLinesSeed);
  }

  private attachClientMock(offer: Offer): Offer {
    const client = clientsSeed.find((c) => c.cr_crm_clientid === offer.cr_crm_clientid);
    return { ...offer, cr_crm_client: client ? { cr_crm_companyname: client.cr_crm_companyname } : undefined };
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  async list(search = '', status?: OfferStatus): Promise<Offer[]> {
    if (apiClient.isConfigured()) {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (status !== undefined) params.set('status', String(status));
      const qs = params.toString();
      const res = await apiClient.get<{ totalCount: number; items: Offer[] } | Offer[]>(
        `/api/offers${qs ? `?${qs}` : ''}`
      );
      const raw = Array.isArray(res) ? res : (res as unknown as { items: Record<string, unknown>[] }).items ?? [];
      return raw.map((o: unknown) => {
        const rec = o as Record<string, unknown>;
        return {
          ...rec,
          cr_crm_client: rec.cr_crm_clientname ? { cr_crm_companyname: rec.cr_crm_clientname } : rec.cr_crm_client,
        };
      }) as Offer[];
    }

    this.ensureSeed();
    await delay();
    const term = search.toLowerCase();
    return readCollection<Offer>(OFFER_ENTITY)
      .filter((o) => (status !== undefined ? o.cr_crm_status === status : true))
      .filter((o) => !term || o.cr_crm_reference.toLowerCase().includes(term) || this.attachClientMock(o).cr_crm_client?.cr_crm_companyname.toLowerCase().includes(term))
      .sort((a, b) => (b.modifiedon ?? '').localeCompare(a.modifiedon ?? ''))
      .map((o) => this.attachClientMock(o));
  }

  async getById(id: string): Promise<OfferWithLines | null> {
    if (apiClient.isConfigured()) {
      const res = await apiClient.get<Record<string, unknown>>(`/api/offers/${id}`);
      if (!res) return null;
      const lines = (res.offerLines ?? res.lines ?? []) as OfferLine[];
      return { ...res, lines } as unknown as OfferWithLines;
    }

    this.ensureSeed();
    await delay();
    const offer = readCollection<Offer>(OFFER_ENTITY).find((o) => o.cr_crm_offerid === id);
    if (!offer) return null;
    const lines = readCollection<OfferLine>(OFFER_LINE_ENTITY)
      .filter((l) => l.cr_crm_offerid === id)
      .sort((a, b) => (a.cr_crm_sortorder ?? 0) - (b.cr_crm_sortorder ?? 0));
    return { ...this.attachClientMock(offer), lines };
  }

  async createDraftTemplate(client?: Client, exchangeRate = 3.158, taxRate = DEFAULT_TAX_RATE): Promise<OfferWithLines> {
    const refs = apiClient.isConfigured()
      ? (await this.list()).map((o) => o.cr_crm_reference)
      : readCollection<Offer>(OFFER_ENTITY).map((o) => o.cr_crm_reference);

    return {
      cr_crm_offerid: createId('offer'),
      cr_crm_reference: generateOfferReference(refs),
      cr_crm_clientid: client?.cr_crm_clientid ?? '',
      cr_crm_client: client ? { cr_crm_companyname: client.cr_crm_companyname } : undefined,
      cr_crm_status: OfferStatus.Brouillon,
      cr_crm_exchangerateid: '',
      cr_crm_exchangerateval: exchangeRate,
      cr_crm_taxrate: client?.cr_crm_vatexempt ? 0 : taxRate,
      cr_crm_validuntil: addDays(new Date(), OFFER_VALIDITY_DAYS).toISOString(),
      cr_crm_notes: '',
      cr_crm_version: 1,
      createdon: new Date().toISOString(),
      modifiedon: new Date().toISOString(),
      lines: [],
    };
  }

  async save(offer: Offer, lines: OfferLine[]): Promise<OfferWithLines> {
    if (apiClient.isConfigured()) {
      const isNew = !offer.cr_crm_offerid || offer.cr_crm_offerid.startsWith('offer-');
      if (isNew) {
        // Map to backend format for creation
        const payload = {
          clientId: offer.cr_crm_clientid,
          exchangeRateId: offer.cr_crm_exchangerateid,
          currency: 'TND',
          taxRate: offer.cr_crm_taxrate,
          vatExempt: false,
          validUntil: offer.cr_crm_validuntil,
          lines: lines.map((l) => ({
            skuId: l.cr_crm_skuid,
            skuName: l.cr_crm_productname ? `${l.cr_crm_productname} — ${l.cr_crm_skuname}` : l.cr_crm_skuname,
            quantity: l.cr_crm_quantity,
            billingCycle: l.cr_crm_billingcycle,
            unitPriceUsd: l.cr_crm_unitpriceusd,
            unitPriceTnd: l.cr_crm_unitpricetnd,
            discountPct: l.cr_crm_discountpct,
            sortOrder: l.cr_crm_sortorder,
          })),
        };
        const raw = await apiClient.post<Record<string, unknown>>('/api/offers', payload);
        const resultLines = (raw.offerLines ?? raw.lines ?? []) as OfferLine[];
        return { ...raw, lines: resultLines } as unknown as OfferWithLines;
      } else {
        // Update offer metadata + lines
        const payload = {
          taxRate: offer.cr_crm_taxrate,
          validUntil: offer.cr_crm_validuntil,
          status: offer.cr_crm_status,
          version: offer.cr_crm_version,
          lines: lines.map((l) => ({
            id: l.cr_crm_offerlineid,
            skuId: l.cr_crm_skuid,
            skuName: l.cr_crm_productname ? `${l.cr_crm_productname} — ${l.cr_crm_skuname}` : l.cr_crm_skuname,
            quantity: l.cr_crm_quantity,
            billingCycle: l.cr_crm_billingcycle,
            unitPriceUsd: l.cr_crm_unitpriceusd,
            unitPriceTnd: l.cr_crm_unitpricetnd,
            discountPct: l.cr_crm_discountpct,
            sortOrder: l.cr_crm_sortorder,
          })),
        };
        const raw2 = await apiClient.put<Record<string, unknown>>(`/api/offers/${offer.cr_crm_offerid}`, payload);
        const updLines = (raw2.offerLines ?? raw2.lines ?? []) as OfferLine[];
        return { ...raw2, lines: updLines } as unknown as OfferWithLines;
      }
    }

    this.ensureSeed();
    await delay(200);
    const offers = readCollection<Offer>(OFFER_ENTITY);
    const existing = offers.find((o) => o.cr_crm_offerid === offer.cr_crm_offerid);
    const normalizedLines = lines.map((line, index) => ({
      ...line,
      cr_crm_offerid: offer.cr_crm_offerid,
      cr_crm_offerlineid: line.cr_crm_offerlineid || createId('offerline'),
      cr_crm_sortorder: index + 1,
    }));
    const persisted: Offer = {
      ...offer,
      createdon: existing?.createdon ?? new Date().toISOString(),
      modifiedon: new Date().toISOString(),
    };
    writeCollection(OFFER_ENTITY, existing
      ? offers.map((o) => (o.cr_crm_offerid === offer.cr_crm_offerid ? persisted : o))
      : [...offers, persisted]);
    writeCollection(OFFER_LINE_ENTITY, [
      ...readCollection<OfferLine>(OFFER_LINE_ENTITY).filter((l) => l.cr_crm_offerid !== offer.cr_crm_offerid),
      ...normalizedLines,
    ]);
    await auditLogService.record({
      actionType: existing ? AuditActionType.Update : AuditActionType.Create,
      entityName: 'Offer',
      entityId: offer.cr_crm_offerid,
      description: `Offer ${offer.cr_crm_reference} ${existing ? 'updated' : 'created'}.`,
    });
    return { ...this.attachClientMock(persisted), lines: normalizedLines };
  }

  async delete(id: string): Promise<void> {
    if (apiClient.isConfigured()) {
      await apiClient.delete(`/api/offers/${id}`);
      return;
    }

    this.ensureSeed();
    await delay();
    writeCollection(OFFER_ENTITY, readCollection<Offer>(OFFER_ENTITY).filter((o) => o.cr_crm_offerid !== id));
    writeCollection(OFFER_LINE_ENTITY, readCollection<OfferLine>(OFFER_LINE_ENTITY).filter((l) => l.cr_crm_offerid !== id));
    await auditLogService.record({
      actionType: AuditActionType.Delete,
      entityName: 'Offer',
      entityId: id,
      description: `Offer ${id} deleted.`,
    });
  }

  async send(id: string, recipientEmail: string, message: string, cc?: string): Promise<OfferWithLines> {
    const current = await this.getById(id);
    if (!current) throw new Error('Offer not found.');

    if (apiClient.isConfigured()) {
      // Call the send endpoint which sends email via Graph API
      await apiClient.post<{ ok: boolean }>(`/api/offers/${id}/send`, { recipientEmail, message, cc });
      // Refresh offer data
      return this.getById(id) as Promise<OfferWithLines>;
    }

    return this.save(
      {
        ...current,
        cr_crm_status: OfferStatus.Envoyee,
        cr_crm_sentat: new Date().toISOString(),
        cr_crm_notes: [current.cr_crm_notes, message].filter(Boolean).join('\n\n'),
      },
      current.lines,
    );
  }

  // ─── Offer lines ──────────────────────────────────────────────────────────

  async addLine(offerId: string, line: Omit<OfferLine, 'cr_crm_offerlineid' | 'cr_crm_offerid'>): Promise<OfferLine> {
    if (apiClient.isConfigured()) {
      return apiClient.post<OfferLine>(`/api/offers/${offerId}/lines`, line);
    }
    const newLine: OfferLine = { ...line, cr_crm_offerlineid: createId('offerline'), cr_crm_offerid: offerId };
    writeCollection(OFFER_LINE_ENTITY, [...readCollection<OfferLine>(OFFER_LINE_ENTITY), newLine]);
    return newLine;
  }

  async updateLine(offerId: string, lineId: string, patch: Partial<OfferLine>): Promise<OfferLine> {
    if (apiClient.isConfigured()) {
      return apiClient.put<OfferLine>(`/api/offers/${offerId}/lines/${lineId}`, patch);
    }
    const lines = readCollection<OfferLine>(OFFER_LINE_ENTITY);
    const updated = { ...(lines.find((l) => l.cr_crm_offerlineid === lineId) ?? {}), ...patch } as OfferLine;
    writeCollection(OFFER_LINE_ENTITY, lines.map((l) => (l.cr_crm_offerlineid === lineId ? updated : l)));
    return updated;
  }

  async deleteLine(offerId: string, lineId: string): Promise<void> {
    if (apiClient.isConfigured()) {
      await apiClient.delete(`/api/offers/${offerId}/lines/${lineId}`);
      return;
    }
    writeCollection(OFFER_LINE_ENTITY, readCollection<OfferLine>(OFFER_LINE_ENTITY).filter((l) => l.cr_crm_offerlineid !== lineId));
  }

  // ─── HTML preview (local computation) ────────────────────────────────────

  async getPreviewHtml(offer: Offer, lines: OfferLine[]): Promise<string> {
    const esc = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const formatTND = (amount: number): string => {
      const fixed = amount.toFixed(3);
      const [intPart, decPart] = fixed.split('.');
      const formatted = intPart!.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return `${formatted},${decPart}`;
    };
    const vatRate = offer.cr_crm_taxrate ?? 0.19;
    const subtotal = lines.reduce((sum, l) => sum + (l.cr_crm_linetotaltnd ?? 0), 0);
    const vatAmount = subtotal * vatRate;
    const total = subtotal + vatAmount;
    const now = new Date();
    const dateStr = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;

    const linesHtml = lines.map((l, idx) => {
      const discount = l.cr_crm_discountpct ?? 0;
      const unitPrice = l.cr_crm_unitpricetnd ?? 0;
      const discountedPrice = discount > 0 ? unitPrice * (1 - discount / 100) : unitPrice;
      const billing = l.cr_crm_billingcycle ? ` <span style="color:#666;font-size:8pt">(${String(l.cr_crm_billingcycle).charAt(0).toUpperCase() + String(l.cr_crm_billingcycle).slice(1).toLowerCase()})</span>` : '';
      return `
      <tr>
        <td style="text-align:center">${idx + 1}</td>
        <td>${esc(l.cr_crm_productname ?? l.cr_crm_skuname)}${billing}<br><span style="color:#999;font-size:8pt">${esc(l.cr_crm_skuname)}</span></td>
        <td style="text-align:center">${l.cr_crm_quantity}</td>
        <td style="text-align:right">${formatTND(discountedPrice)}</td>
        <td style="text-align:center">${(vatRate * 100).toFixed(0)}%</td>
        <td style="text-align:right">${formatTND(l.cr_crm_linetotaltnd ?? 0)}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family:'Segoe UI',Arial,sans-serif; font-size:10pt; color:#333; padding:30px; }
      .header { display:flex; justify-content:space-between; align-items:center; margin-bottom:30px; }
      .title-block { text-align:right; }
      .title-block h1 { font-size:28pt; font-weight:700; color:#E5A100; }
      .title-block .ref { font-size:11pt; color:#666; }
      .info-row { display:flex; justify-content:space-between; margin-bottom:25px; border:1px solid #E5A100; border-top:4px solid #E5A100; padding:15px 20px; }
      .client-box { width:55%; }
      .client-box h3 { font-size:11pt; font-weight:700; margin-bottom:8px; }
      .client-box .name { font-size:12pt; font-weight:600; margin-bottom:4px; }
      .client-box .detail { font-size:9.5pt; color:#333; margin-bottom:2px; }
      .client-box .lbl { color:#E5A100; font-weight:600; }
      .date-box { text-align:right; padding-top:10px; font-size:12pt; font-weight:600; }
      table { width:100%; border-collapse:collapse; margin-bottom:20px; border:1px solid #E5A100; }
      thead th { background:#FFF3D6; color:#333; padding:10px 8px; font-size:9pt; font-weight:700; border-bottom:2px solid #E5A100; }
      thead tr { border-top:4px solid #E5A100; }
      tbody td { padding:10px 8px; border-bottom:1px solid #eee; font-size:9.5pt; }
      .totals-row { display:flex; justify-content:space-between; gap:20px; margin-bottom:20px; }
      .totals-row table { width:48%; }
      .vat-table { border:1px solid #E5A100; border-top:4px solid #E5A100; }
      .vat-table th { background:#FFF3D6; padding:8px 12px; font-size:9pt; font-weight:700; }
      .vat-table td { padding:8px 12px; font-size:9.5pt; }
      .summary-table { border:1px solid #E5A100; }
      .summary-table td { padding:8px 12px; font-size:9.5pt; border-bottom:1px solid #eee; }
      .summary-table td:first-child { font-weight:600; }
      .grand-total { display:flex; justify-content:flex-end; margin-bottom:40px; }
      .grand-total-box { background:#FFF3D6; border:2px solid #E5A100; padding:12px 24px; font-size:14pt; font-weight:700; }
      .footer { border-top:2px solid #E5A100; padding-top:15px; margin-top:40px; display:flex; justify-content:space-between; font-size:8pt; color:#666; }
      .footer h4 { font-size:8pt; font-weight:700; color:#333; text-transform:uppercase; margin-bottom:4px; }
      .footer .lbl { color:#E5A100; }
    </style></head><body>
    <div class="header">
      <div><img src="/assets/cloudshift-logo.png" alt="CloudShift" style="height:45px;" /></div>
      <div class="title-block"><h1>DEVIS</h1><div class="ref">N° ${esc(offer.cr_crm_reference)}</div></div>
    </div>
    <div class="info-row">
      <div class="client-box">
        <h3>CLIENT</h3>
        <div class="name">${esc(offer.cr_crm_client?.cr_crm_companyname ?? 'Non sélectionné')}</div>
        ${offer.cr_crm_client?.cr_crm_address ? `<div class="detail">${esc(offer.cr_crm_client.cr_crm_address)}</div>` : ''}
        ${offer.cr_crm_client?.cr_crm_city ? `<div class="detail">${esc([offer.cr_crm_client.cr_crm_postalcode, offer.cr_crm_client.cr_crm_city, offer.cr_crm_client.cr_crm_country].filter(Boolean).join(' '))}</div>` : ''}
        ${offer.cr_crm_client?.cr_crm_vatnumber ? `<div class="detail"><span class="lbl">M/F</span> ${esc(offer.cr_crm_client.cr_crm_vatnumber)}</div>` : ''}
        ${offer.cr_crm_client?.cr_crm_email ? `<div class="detail"><span class="lbl">@</span> ${esc(offer.cr_crm_client.cr_crm_email)}</div>` : ''}
        ${offer.cr_crm_client?.cr_crm_phone ? `<div class="detail"><span class="lbl">M</span> ${esc(offer.cr_crm_client.cr_crm_phone)}</div>` : ''}
      </div>
      <div class="date-box"><strong>Date:</strong> ${dateStr}</div>
    </div>
    <table>
      <thead><tr><th style="width:5%">#</th><th style="width:40%">Description</th><th style="width:10%">Quantité</th><th style="width:18%">PU HT</th><th style="width:9%">TVA</th><th style="width:18%">Total HT</th></tr></thead>
      <tbody>${linesHtml}</tbody>
    </table>
    <div class="totals-row">
      <table class="vat-table"><thead><tr><th>TVA</th><th>Base</th><th>Montant</th></tr></thead><tbody><tr><td>${(vatRate * 100).toFixed(0)} %</td><td>${formatTND(subtotal)} DT</td><td>${formatTND(vatAmount)} DT</td></tr></tbody></table>
      <table class="summary-table"><tbody><tr><td>TOTAL HT</td><td style="text-align:right">${formatTND(subtotal)} DT</td></tr><tr><td>BASE TVA</td><td style="text-align:right">${formatTND(subtotal)} DT</td></tr><tr><td>TOTAL TVA</td><td style="text-align:right">${formatTND(vatAmount)} DT</td></tr></tbody></table>
    </div>
    <div class="grand-total"><div class="grand-total-box"><strong>NET À PAYER</strong>&nbsp;&nbsp;&nbsp;&nbsp;${formatTND(total)} DT</div></div>
    <div class="footer">
      <div><h4>CLOUDSHIFT</h4>Immeuble Lac d'Or "Business Center", Bloc C,<br>6ème étage Bur C6-1 et C6-2<br>1053 Les Berges du Lac II-Tunis-Tunisie<br>Code TVA : 1586027X</div>
      <div><h4>INFORMATIONS DE CONTACT</h4><span class="lbl">M</span> +21624146000<br><span class="lbl">@</span> contact@cloudshift.fr</div>
      <div><h4>INFORMATIONS BANCAIRES</h4>Banque Attijari Bank<br>SWIFT/BIC BSTUTNTTXXX<br>IBAN TN59 04 015 101 0055894497 51</div>
    </div>
    </body></html>`;
    return DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true, FORCE_BODY: false });
  }
}

export const offerService = new OfferService();

